/**
 * Situação de um setor (ao vivo pela telemetria ou numa hora da simulação) — Nexora Grid (US-405).
 *
 * Criado em: 16/10/2026 · Última modificação: 16/10/2026 · Versão: 1.0.0
 *
 * Junta, para o painel do setor e para o chat com o agente do setor:
 * - reservatórios (nível simulado);
 * - pressões (mínima, média, máxima, % dentro da NBR 12218);
 * - macromedidores (vazão do trecho onde estão);
 * - bombas e poços (estado simulado pelo nível);
 * - ocorrências e falhas derivadas dos números.
 * Tudo é SIMULAÇÃO até existir telemetria real e calibração (documento 13 §15).
 */
import type { Equipamento, Rede } from "./rede";
import { estadoBomba, interpolar, NBR_MAX, NBR_MIN, resumoPressao, type Simulacao } from "./simulacao";

export type Ocorrencia = { prioridade: 1 | 2 | 3 | 4; texto: string };
export type SituacaoSetor = {
  setor: string;
  /** de onde vêm os números: "ao vivo" (telemetria) ou "simulação" */
  origem: string;
  hora: number;
  trechos: number;
  casas: number;
  reservatorios: { id: string; volume: number; nivel: number }[];
  pressao: ReturnType<typeof resumoPressao>;
  macromedidores: { id: string; vazaoLps: number }[];
  bombas: { id: string; tipo: string; ligada: boolean }[];
  registrosFechados: number;
  ocorrencias: Ocorrencia[];
};

export function volumeDe(e: Equipamento): number {
  return Number(e.detalhe.match(/\d+/)?.[0] ?? 0);
}

/** Calcula a situação do setor na hora (fracionária) indicada. */
export function situacaoSetor(rede: Rede, sim: Simulacao, niveis: Map<string, number[]>, nosDoSetor: Set<string>,
  setor: string, hora: number): SituacaoSetor {
  const h = Math.floor(hora) % sim.horas;
  const doSetor = rede.equipamentos.filter((e) => e.setor === setor);
  const reservatorios = doSetor.filter((e) => e.tipo === "reservatorio")
    .map((e) => ({ id: e.id, volume: volumeDe(e), nivel: niveis.has(e.id) ? interpolar(niveis.get(e.id)!, hora) : NaN }));
  const nivelReferencia = niveis.get(reservatorios[0]?.id ?? "") ?? [...niveis.values()][0] ?? [];
  const estados = estadoBomba(nivelReferencia);
  const bombas = doSetor.filter((e) => ["bomba", "poco_profundo", "elevatoria"].includes(e.tipo))
    .map((e) => ({ id: e.id || e.tipo, tipo: e.tipo, ligada: estados[h] ?? true }));
  const macromedidores = doSetor.filter((e) => e.tipo === "macromedidor")
    .map((e) => ({ id: e.id || "macromedidor", vazaoLps: Math.abs(sim.vazoes[h].get(e.trecho) ?? 0) }));
  const pressao = resumoPressao(sim.pressoes[h], nosDoSetor);
  let casas = 0;
  nosDoSetor.forEach((id) => { casas += rede.nos.get(id)?.casas ?? 0; });
  const ocorrencias: Ocorrencia[] = [];
  let abaixo = 0, acima = 0;
  nosDoSetor.forEach((id) => {
    const p = sim.pressoes[h].get(id);
    if (p === undefined) return;
    if (p < NBR_MIN) abaixo++;
    if (p > NBR_MAX) acima++;
  });
  if (abaixo) ocorrencias.push({ prioridade: abaixo > 20 ? 1 : 2, texto: `${abaixo} pontos abaixo de ${NBR_MIN} mca (NBR 12218)` });
  for (const r of reservatorios) {
    if (r.nivel < 20) ocorrencias.push({ prioridade: 1, texto: `${r.id}: nível crítico (${r.nivel.toFixed(0)}%)` });
    else if (r.nivel < 35) ocorrencias.push({ prioridade: 2, texto: `${r.id}: nível baixo (${r.nivel.toFixed(0)}%)` });
  }
  if (acima) ocorrencias.push({ prioridade: 3, texto: `${acima} pontos acima de ${NBR_MAX} mca (risco de vazamento)` });
  const registrosFechados = doSetor.filter((e) => e.tipo === "registro_fechado").length;
  if (!macromedidores.length) ocorrencias.push({ prioridade: 4, texto: "setor sem macromedidor: perdas não medidas" });
  ocorrencias.push({ prioridade: 4, texto: "sem telemetria real ligada: valores da simulação, rede não calibrada" });
  return {
    setor, origem: "simulação", hora, trechos: rede.trechos.filter((t) => t.setor === setor).length, casas, reservatorios, pressao,
    macromedidores, bombas, registrosFechados, ocorrencias: ocorrencias.sort((a, b) => a.prioridade - b.prioridade),
  };
}

export type GrupoAlarmeSetor = { device_id: string; tipo: string; prioridade: 1 | 2 | 3 | 4; ocorrencias: number; reconhecido?: boolean;
  descricao?: string };

/**
 * Situação AO VIVO do setor (retorno de 17/10/2026: o SCADA 3D mostra dados ao vivo; a simulação fica na tela Rede).
 * Pressão: a medida pelos macromedidores do setor; ocorrências: os alarmes reais dos dispositivos do setor e os sem leitura.
 */
export function situacaoSetorAoVivo(rede: Rede, setor: string, nosDoSetor: Set<string>,
  ler: (device: string | null | undefined, medida: string) => number | null, velhos: Set<string>,
  alarmes: GrupoAlarmeSetor[]): SituacaoSetor {
  const agora = new Date();
  const doSetor = rede.equipamentos.filter((e) => e.setor === setor);
  const reservatorios = doSetor.filter((e) => e.tipo === "reservatorio")
    .map((e) => ({ id: e.id, volume: volumeDe(e), nivel: ler(e.deviceId, "nivel_reservatorio_pct") ?? NaN }));
  const macros = doSetor.filter((e) => e.tipo === "macromedidor");
  const macromedidores = macros.map((e) => ({ id: e.id || "macromedidor", vazaoLps: (ler(e.deviceId, "vazao_instantanea_m3h") ?? 0) / 3.6 }));
  const pressoesMedidas = macros.map((e) => ler(e.deviceId, "pressao_rede_mca")).filter((v): v is number => v !== null);
  const pressao = pressoesMedidas.length ? {
    min: Math.min(...pressoesMedidas), max: Math.max(...pressoesMedidas),
    media: pressoesMedidas.reduce((a, b) => a + b, 0) / pressoesMedidas.length,
    dentroPct: (pressoesMedidas.filter((p) => p >= NBR_MIN && p <= NBR_MAX).length / pressoesMedidas.length) * 100,
    nos: pressoesMedidas.length } : null;
  const bombas = doSetor.filter((e) => ["bomba", "poco_profundo", "elevatoria"].includes(e.tipo)).map((e) => {
    const estado = ler(e.deviceId, "bomba_ligada");
    const potencia = ler(e.deviceId, "potencia_ativa_kw");
    return { id: e.id || e.tipo, tipo: e.tipo, ligada: estado !== null ? estado >= 0.5 : (potencia ?? 0) > 0.5 };
  });
  let casas = 0;
  nosDoSetor.forEach((id) => { casas += rede.nos.get(id)?.casas ?? 0; });
  const devices = new Map(doSetor.filter((e) => e.deviceId).map((e) => [e.deviceId!, e.id]));
  const ocorrencias: Ocorrencia[] = alarmes.filter((a) => devices.has(a.device_id))
    .map((a) => ({ prioridade: a.prioridade, texto: `${devices.get(a.device_id)}: ${a.tipo.replace(/_/g, " ").toLowerCase()} (${a.ocorrencias}×${a.reconhecido ? ", reconhecido" : ""})` }));
  const semLeitura = doSetor.filter((e) => e.deviceId && velhos.has(e.deviceId)).map((e) => e.id);
  if (semLeitura.length) ocorrencias.push({ prioridade: 2, texto: `sem comunicação: ${semLeitura.join(", ")}` });
  const semDispositivo = doSetor.filter((e) => !e.deviceId && e.tipo !== "registro" && e.tipo !== "registro_fechado").length;
  if (semDispositivo) ocorrencias.push({ prioridade: 4, texto: `${semDispositivo} equipamento(s) sem telemetria ligada` });
  if (!macros.length) ocorrencias.push({ prioridade: 4, texto: "setor sem macromedidor: perdas não medidas" });
  return {
    setor, origem: "ao vivo", hora: agora.getHours() + agora.getMinutes() / 60, trechos: rede.trechos.filter((t) => t.setor === setor).length,
    casas, reservatorios, pressao, macromedidores, bombas, registrosFechados: doSetor.filter((e) => e.tipo === "registro_fechado").length,
    ocorrencias: ocorrencias.sort((a, b) => a.prioridade - b.prioridade),
  };
}
